import { Platform } from "react-native";
import Clarity from "@microsoft/clarity";

/**
 * ClarityManager
 * Manages Microsoft Clarity analytics for sessions that start with onboarding.
 *
 * Design principles:
 * - Only tracks sessions that BEGIN with onboarding (new user signup)
 * - Does NOT use persistent storage (no localStorage, no cookies)
 * - Does NOT initialize Clarity until user accepts cookies
 * - Only sends data to Clarity servers when user accepts cookies
 * - Continues tracking even AFTER onboarding completes (throughout the session)
 * - If user reloads page, tracking stops (session-only, no persistence)
 * - Session-only tracking - no data persisted across page reloads
 */
class ClarityManager {
  constructor() {
    // 🧪 EXPERIMENT: Auto-consent for onboarding tracking test
    // Set to false to restore cookie panel behavior
    this.AUTO_CONSENT_EXPERIMENT = false;

    this.isInitialized = false;
    this.isTracking = false;
    this.projectId = null;
    this.hasConsent = null; // null = not decided, true = accepted, false = rejected
    this.pendingEvents = []; // Store events until consent is granted
    this.pendingTags = {}; // Store tags until consent is granted
    this.userId = null; // Store user ID until consent is granted
  }

  /**
   * Prepare Clarity for initialization (but don't start yet)
   * Store the project ID and user context, but wait for consent
   * @param {string} projectId - Your Clarity project ID
   */
  init(projectId) {
    // Only works on web
    if (Platform.OS !== "web") {
      console.log(
        "[ClarityManager] Not on web platform, skipping initialization"
      );
      return;
    }

    // Store project ID for later initialization
    this.projectId = projectId;

    // 🧪 EXPERIMENT: Auto-grant consent if experiment flag is enabled
    if (this.AUTO_CONSENT_EXPERIMENT) {
      console.log("[ClarityManager] EXPERIMENT MODE");
      // Defer to next tick to allow tags/events to be queued first
      setTimeout(() => this.grantConsent(), 0);
      return;
    }

    console.log("[ClarityManager] Ready to initialize (waiting for consent)");
  }

  /**
   * Grant consent and send collected data to Clarity
   * This should be called when user clicks "Accept all"
   * This is when Clarity actually initializes and starts recording
   */
  grantConsent() {
    if (!this.projectId) {
      console.warn(
        "[ClarityManager] Cannot grant consent - not prepared (no project ID)"
      );
      return;
    }

    if (this.isInitialized) {
      console.log("[ClarityManager] Already initialized with consent");
      return;
    }

    try {
      // NOW initialize Clarity - only after consent is granted
      Clarity.init(this.projectId);
      Clarity.consent(true); // Ensure consent is recorded
      this.isInitialized = true;
      this.isTracking = true;
      this.hasConsent = true;

      console.log("[ClarityManager] Consent granted - Clarity initialized");

      // Apply all pending tags that were set before consent
      this.setTag("session_type", "onboarding");
      this.setTag("session_started_with", "onboarding");
      this.setTag("cookie_consent", "accepted");

      Object.keys(this.pendingTags).forEach((key) => {
        this.setTag(key, this.pendingTags[key]);
      });
      this.pendingTags = {};

      // Apply user identification if it was set before consent
      if (this.userId) {
        Clarity.identify(this.userId);
      }

      // Replay all pending events that happened before consent
      this.pendingEvents.forEach((eventName) => {
        Clarity.event(eventName);
      });
      this.pendingEvents = [];

      // Upgrade session to ensure it's recorded
      Clarity.upgrade("user_accepted_cookies");
    } catch (error) {
      console.error("[ClarityManager] Failed to grant consent:", error);
    }
  }

  /**
   * Deny consent and stop tracking
   * This should be called when user clicks "Reject all"
   * Clarity will never be initialized, so no data is collected or sent
   */
  denyConsent() {
    if (this.isInitialized) {
      console.warn(
        "[ClarityManager] Clarity was already initialized - cannot deny consent retroactively"
      );
      return;
    }

    this.hasConsent = false;
    this.isTracking = false;

    console.log(
      "[ClarityManager] Consent denied - Clarity will NOT be initialized"
    );

    // Clear any pending data
    this.pendingEvents = [];
    this.pendingTags = {};
    this.userId = null;
    this.projectId = null;
  }

  /**
   * Stop tracking completely
   * Call this manually if you need to stop tracking mid-session
   * Note: Normally, tracking continues for the entire browser session
   */
  stop() {
    if (!this.isTracking) {
      return;
    }

    this.isTracking = false;
    console.log("[ClarityManager] Stopped tracking (manual stop called)");

    // Note: Clarity doesn't have a built-in stop method
    // The session will naturally end when the page is closed/reloaded
    // This just prevents our wrapper from making further API calls
  }

  /**
   * Set a custom tag for filtering in Clarity dashboard
   * If consent not yet granted, tags are queued and applied after consent
   * @param {string} key - Tag key
   * @param {string|string[]} value - Tag value(s)
   */
  setTag(key, value) {
    if (!this.isInitialized) {
      // Queue tags until Clarity is initialized (after consent)
      this.pendingTags[key] = value;
      return;
    }

    if (!this.isTracking) {
      return;
    }

    try {
      Clarity.setTag(key, value);
    } catch (error) {
      console.error(`[ClarityManager] Failed to set tag ${key}:`, error);
    }
  }

  /**
   * Log a custom event
   * If consent not yet granted, events are queued and replayed after consent
   * @param {string} eventName - Name of the event
   */
  event(eventName) {
    if (!this.isInitialized) {
      // Queue events until Clarity is initialized (after consent)
      this.pendingEvents.push(eventName);
      return;
    }

    if (!this.isTracking) {
      return;
    }

    try {
      Clarity.event(eventName);
    } catch (error) {
      console.error(
        `[ClarityManager] Failed to log event ${eventName}:`,
        error
      );
    }
  }

  /**
   * Identify user with custom ID
   * If consent not yet granted, user ID is stored and applied after consent
   * @param {string} userId - User ID
   */
  identify(userId) {
    // Store user ID for later (when consent is granted)
    this.userId = userId;

    if (!this.isInitialized) {
      // Will be applied when Clarity initializes after consent
      return;
    }

    if (!this.isTracking) {
      return;
    }

    try {
      Clarity.identify(userId);
    } catch (error) {
      console.error("[ClarityManager] Failed to identify user:", error);
    }
  }

  /**
   * Get current consent status
   * @returns {boolean|null} null = not decided, true = accepted, false = rejected
   */
  getConsentStatus() {
    return this.hasConsent;
  }

  /**
   * Check if tracking is active
   * @returns {boolean}
   */
  isActive() {
    return this.isTracking;
  }

  /**
   * Add masking to elements by CSS selector (via Clarity dashboard)
   * Note: This is a helper to remind you to configure masking in the Clarity dashboard
   * Go to: Clarity Dashboard > Settings > Masking > Mask by element
   * Add CSS selectors like: .coach-logo, #sensitive-data, etc.
   */
  static getMaskingInstructions() {
    return {
      dashboardSetup: "Go to Clarity Dashboard > Settings > Masking",
      modes: {
        strict: "Entire content is masked",
        balanced: "Only sensitive content (numbers, emails) masked",
        relaxed: "No content masked (input boxes still masked)",
      },
      htmlAttribute:
        "Add data-clarity-mask='true' to React Native View/Image components",
      cssSelectors:
        "In Clarity dashboard, add CSS selectors to mask specific elements",
      note: "Changes take up to 1 hour to apply to new recordings",
    };
  }
}

// Export singleton instance
export default new ClarityManager();
